import { Box, Flex } from '@chakra-ui/react';
import { Drawer } from '@/components/ats';
import { FormBuilder } from '@/components/formBuilder';
import { usePushToHris } from '@/components/ats/hris/useHrisIntegrations';
import { useCallback, useMemo, useRef } from 'react';
import DocumentsSection from '@/components/ats/hris/document-section/DocumentsSection';

// HRIS integrations that support document upload
const DOCUMENT_UPLOAD_SUPPORTED_INTEGRATIONS = new Set([
  'personio',
  'workday',
  'afas',
  'bamboohr',
  'hibob',
  'nmbrs',
  'sagehr',
  'hrworks',
  'youserve',
  'sdworx',
]);

const EmployeeFormDrawer = ({
  isOpen,
  onClose,
  integrationName,
  integration,
  applicationId,
  formConfig,
  onSubmit = () => {},
  onSuccess = () => {},
  formRef,
  initialValues = {},
}) => {
  const { submitEmployeeForm, isPending } = usePushToHris({
    onSuccess: onSuccess,
  });

  const documentsRef = useRef(null);

  // Check if the integration supports document upload
  const showDocumentsSection = useMemo(() => {
    const integrationType = integration?.type || '';
    // Extract the tool ID from 'kombo/{id}' format
    const toolId = integrationType.startsWith('kombo/')
      ? integrationType.replace('kombo/', '')
      : integrationType;
    return DOCUMENT_UPLOAD_SUPPORTED_INTEGRATIONS.has(toolId);
  }, [integration?.type]);

  const handleSubmitForm = useCallback(async () => {
    try {
      await formRef.current?.submitForm();
      const errArray = Object.keys(formRef.current?.errors);
      if (errArray?.length) {
        const [firstField] = errArray;
        const $element = document.querySelector(`label[for="${firstField}"]`);
        $element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      if (!formRef.current?.isValid) {
        return false;
      }

      const success = await submitEmployeeForm(integration, applicationId, {
        ...(formRef.current?.values || {}),
        hris_documents: showDocumentsSection
          ? documentsRef.current?.getDocumentsData?.() || []
          : [],
      });

      if (success) {
        if (onSubmit) {
          await onSubmit();
        }
      }

      return success;
    } catch {
      onClose();
      return false;
    }
  }, [
    formRef,
    submitEmployeeForm,
    integration,
    applicationId,
    onClose,
    onSubmit,
    showDocumentsSection,
  ]);

  if (!isOpen) return null;

  const firstName = initialValues?.first_name || initialValues?.firstName || '';
  const lastName = initialValues?.last_name || initialValues?.lastName || '';
  const name = firstName && (lastName ? `${firstName} ${lastName}` : firstName);

  return (
    <Drawer
      drawerActions={{
        isOpen,
        onClose,
      }}
      headerText={`Add ${name || 'employee'} to ${integrationName}`}
      secondaryCtaLabel="Cancel"
      secondaryCtaClick={onClose}
      ctaLabel="Add employee"
      isCtaLoading={isPending}
      onDrawerSubmit={handleSubmitForm}
      ctaProps={{
        isDisabled: isPending,
        minW: '170px',
      }}
      loadingText="Adding"
      hasFooterDivider={true}
      headerTextProps={{
        borderBottom: '1px solid',
        borderBottomColor: 'border-color',
      }}
      drawerBodyProps={{
        overflowY: 'auto',
      }}
    >
      <Flex w="100%" h="100%" flex={1} direction="column" overflow="hidden">
        <Box flex={1} overflowY="auto">
          <FormBuilder
            config={formConfig}
            formRef={formRef}
            initialValues={initialValues}
          />
          {showDocumentsSection && (
            <DocumentsSection
              ref={documentsRef}
              integration={integration}
              applicationId={applicationId}
            />
          )}
        </Box>
      </Flex>
    </Drawer>
  );
};

export default EmployeeFormDrawer;
